import { loadBrowserImage } from './browserImages';

const OCCLUSION_ALPHA_THRESHOLD = 10;
const OCCLUSION_RGB_THRESHOLD = 128;

export const MIN_OCCLUSION_BRUSH_SIZE = 1;
export const MAX_OCCLUSION_BRUSH_SIZE = 96;
export const DEFAULT_OCCLUSION_OVERLAY_OPACITY = 0.45;
export const DEFAULT_OCCLUSION_BRUSH_SIZE = 16;

export type OcclusionMaskPaintState = 'occlude' | 'clear';

interface CanvasPixelSizeInput {
  canvasHeight: number;
  canvasWidth: number;
  tileSize: number;
}

interface OcclusionBrushRectInput {
  brushSize: number;
  height: number;
  width: number;
  x: number;
  y: number;
}

export const clampOcclusionBrushSize = (size: number): number =>
  Math.max(MIN_OCCLUSION_BRUSH_SIZE, Math.min(MAX_OCCLUSION_BRUSH_SIZE, Math.round(size)));

export const getOcclusionMaskPixelSize = ({
  canvasHeight,
  canvasWidth,
  tileSize,
}: CanvasPixelSizeInput): { height: number; width: number } => ({
  height: Math.max(1, Math.floor(canvasHeight * tileSize)),
  width: Math.max(1, Math.floor(canvasWidth * tileSize)),
});

export const getOcclusionBrushRect = ({
  brushSize,
  height,
  width,
  x,
  y,
}: OcclusionBrushRectInput): { height: number; width: number; x: number; y: number } => {
  const size = clampOcclusionBrushSize(brushSize);
  const startX = Math.max(0, Math.min(width - 1, Math.floor(x - (size - 1) / 2)));
  const startY = Math.max(0, Math.min(height - 1, Math.floor(y - (size - 1) / 2)));
  const endX = Math.max(startX, Math.min(width, startX + size));
  const endY = Math.max(startY, Math.min(height, startY + size));
  return {
    x: startX,
    y: startY,
    width: endX - startX,
    height: endY - startY,
  };
};

export const isOcclusionMaskPixel = (
  red: number,
  green: number,
  blue: number,
  alpha: number,
): boolean =>
  alpha >= OCCLUSION_ALPHA_THRESHOLD &&
  red >= OCCLUSION_RGB_THRESHOLD &&
  green >= OCCLUSION_RGB_THRESHOLD &&
  blue >= OCCLUSION_RGB_THRESHOLD;

const createCanvas = (width: number, height: number): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

const toDataUrl = (canvas: HTMLCanvasElement): string => canvas.toDataURL('image/png');

const hasVisiblePixels = (
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
): boolean => {
  const pixelData = context.getImageData(0, 0, width, height).data;
  for (let index = 3; index < pixelData.length; index += 4) {
    if ((pixelData[index] ?? 0) >= OCCLUSION_ALPHA_THRESHOLD) {
      return true;
    }
  }
  return false;
};

const normalizeMaskPixels = (
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
): boolean => {
  const imageData = context.getImageData(0, 0, width, height);
  const { data } = imageData;
  let hasMaskPixels = false;
  for (let index = 0; index < data.length; index += 4) {
    const red = data[index] ?? 0;
    const green = data[index + 1] ?? 0;
    const blue = data[index + 2] ?? 0;
    const alpha = data[index + 3] ?? 0;
    if (isOcclusionMaskPixel(red, green, blue, alpha)) {
      data[index] = 255;
      data[index + 1] = 255;
      data[index + 2] = 255;
      data[index + 3] = 255;
      hasMaskPixels = true;
    } else {
      data[index] = 0;
      data[index + 1] = 0;
      data[index + 2] = 0;
      data[index + 3] = 0;
    }
  }
  context.putImageData(imageData, 0, 0);
  return hasMaskPixels;
};

export const createNormalizedOcclusionMaskCanvas = async ({
  height,
  imagePath,
  width,
}: {
  height: number;
  imagePath: string;
  width: number;
}): Promise<{ canvas: HTMLCanvasElement; hasVisiblePixels: boolean }> => {
  const maskCanvas = createCanvas(width, height);
  const context = maskCanvas.getContext('2d', { willReadFrequently: true });
  if (!context || !imagePath.trim()) {
    return { canvas: maskCanvas, hasVisiblePixels: false };
  }

  let image: HTMLImageElement;
  try {
    image = await loadBrowserImage(imagePath);
  } catch (error: unknown) {
    console.warn('[level-editor] Occlusion mask image failed to load', error);
    return { canvas: maskCanvas, hasVisiblePixels: false };
  }
  context.imageSmoothingEnabled = false;
  context.clearRect(0, 0, width, height);
  context.drawImage(image, 0, 0, image.width, image.height, 0, 0, width, height);
  return {
    canvas: maskCanvas,
    hasVisiblePixels: normalizeMaskPixels(context, width, height),
  };
};

export const importOcclusionMaskImage = async ({
  canvasHeight,
  canvasWidth,
  imagePath,
  tileSize,
}: CanvasPixelSizeInput & {
  imagePath: string;
}): Promise<{ maskUrl: string | null; occludingPixelCount: number }> => {
  const { height, width } = getOcclusionMaskPixelSize({ canvasHeight, canvasWidth, tileSize });
  const { canvas, hasVisiblePixels: hasMaskPixels } = await createNormalizedOcclusionMaskCanvas({
    height,
    imagePath,
    width,
  });
  if (!hasMaskPixels) {
    return { maskUrl: null, occludingPixelCount: 0 };
  }

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    return { maskUrl: null, occludingPixelCount: 0 };
  }

  const data = context.getImageData(0, 0, width, height).data;
  let occludingPixelCount = 0;
  for (let index = 3; index < data.length; index += 4) {
    if ((data[index] ?? 0) >= OCCLUSION_ALPHA_THRESHOLD) {
      occludingPixelCount += 1;
    }
  }

  return { maskUrl: toDataUrl(canvas), occludingPixelCount };
};

export const paintOcclusionMask = async ({
  brushSize,
  canvasHeight,
  canvasWidth,
  maskUrl,
  paintState,
  tileSize,
  x,
  y,
}: CanvasPixelSizeInput & {
  brushSize: number;
  maskUrl: string | null;
  paintState: OcclusionMaskPaintState;
  x: number;
  y: number;
}): Promise<string | null> => {
  const { height, width } = getOcclusionMaskPixelSize({ canvasHeight, canvasWidth, tileSize });
  const canvas = createCanvas(width, height);
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    return maskUrl;
  }

  if (maskUrl?.trim()) {
    const existingMask = await createNormalizedOcclusionMaskCanvas({
      height,
      imagePath: maskUrl,
      width,
    });
    context.drawImage(existingMask.canvas, 0, 0);
  }

  const rect = getOcclusionBrushRect({ brushSize, height, width, x, y });
  if (paintState === 'clear') {
    context.clearRect(rect.x, rect.y, rect.width, rect.height);
  } else {
    context.fillStyle = '#ffffff';
    context.fillRect(rect.x, rect.y, rect.width, rect.height);
  }

  return hasVisiblePixels(context, width, height) ? toDataUrl(canvas) : null;
};

export const drawOcclusionForegroundPixels = async ({
  backgroundCanvas,
  foregroundCanvas,
  occlusionMaskUrl,
}: {
  backgroundCanvas: HTMLCanvasElement;
  foregroundCanvas: HTMLCanvasElement;
  occlusionMaskUrl: string | null;
}): Promise<boolean> => {
  if (!occlusionMaskUrl?.trim()) {
    return false;
  }

  const width = backgroundCanvas.width;
  const height = backgroundCanvas.height;
  if (foregroundCanvas.width !== width) {
    foregroundCanvas.width = width;
  }
  if (foregroundCanvas.height !== height) {
    foregroundCanvas.height = height;
  }

  const foregroundContext = foregroundCanvas.getContext('2d');
  if (!foregroundContext) {
    return false;
  }
  const { canvas: maskCanvas, hasVisiblePixels: hasMaskPixels } =
    await createNormalizedOcclusionMaskCanvas({
      height,
      imagePath: occlusionMaskUrl,
      width,
    });
  if (!hasMaskPixels) {
    return false;
  }

  foregroundContext.imageSmoothingEnabled = false;
  foregroundContext.drawImage(backgroundCanvas, 0, 0);
  foregroundContext.globalCompositeOperation = 'destination-in';
  foregroundContext.drawImage(maskCanvas, 0, 0);
  foregroundContext.globalCompositeOperation = 'source-over';
  return true;
};
